import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
  CreditCard,
  Plus,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  User,
  Shield,
  History,
  Tag,
  Sparkles,
  AlertCircle,
  ChevronRight,
  Layers,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Button,
  Input,
  Select,
  Checkbox,
  Modal,
  Tabs,
  useToast,
} from '../ui';

export interface SubscriptionPlan {
  id: number;
  sector_id: number;
  sector_name?: string;
  name: string;
  code: string;
  description: string;
  price: number;
  currency: string;
  duration_days: number;
  status: 'active' | 'inactive';
  entitlements?: string[];
}

export interface UserSubscription {
  id: number;
  user_id: number;
  user_name?: string;
  user_mobile?: string;
  plan_id: number;
  plan_name?: string;
  sector_id: number;
  sector_name?: string;
  status: 'active' | 'cancelled' | 'expired';
  start_at: string;
  end_at: string;
  provider: string;
}

export interface SubscriptionTransaction {
  id: number;
  user_id: number;
  user_name?: string;
  plan_id: number;
  plan_name?: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  transaction_type: string;
  created_at: string;
}

export const SubscriptionManager: React.FC = () => {
  const { sectors, users } = useApp();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState<'plans' | 'subscriptions' | 'transactions'>('plans');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [transactions, setTransactions] = useState<SubscriptionTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modal States
  const [isCreatePlanModalOpen, setIsCreatePlanModalOpen] = useState(false);
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);

  // Form State for Plan Creation
  const [newPlan, setNewPlan] = useState({
    sector_id: sectors[0]?.id || 1,
    name: '',
    code: '',
    description: '',
    price: 499,
    duration_days: 365,
    entitlements: ['practice_access', 'revision_access', 'mock_test_access'],
  });

  // Form State for Manual Grant
  const [grantData, setGrantData] = useState({
    user_id: users[0]?.id || 1,
    plan_id: 0,
    duration_days: 365,
  });

  const fetchPlans = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/plans.php');
      const data = await res.json();
      if (data.success) {
        setPlans(data.plans || []);
        if (data.plans.length > 0 && !grantData.plan_id) {
          setGrantData((prev) => ({ ...prev, plan_id: data.plans[0].id }));
        }
      }
    } catch (e) {
      console.error('Error fetching plans:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubscriptionsAndTx = async () => {
    try {
      const res = await fetch('/api/subscriptions.php?action=list_all');
      const data = await res.json();
      if (data.success) {
        setSubscriptions(data.subscriptions || []);
        setTransactions(data.transactions || []);
      }
    } catch (e) {
      console.error('Error fetching subscriptions:', e);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchSubscriptionsAndTx();
  }, []);

  const handleCreatePlan = async () => {
    if (!newPlan.name || !newPlan.code) {
      showToast('Plan Name and Code are required', 'error');
      return;
    }

    try {
      const res = await fetch('/api/plans.php?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPlan),
      });
      const data = await res.json();

      if (data.success) {
        showToast('Subscription plan created successfully', 'success');
        setIsCreatePlanModalOpen(false);
        fetchPlans();
      } else {
        showToast(data.error || 'Failed to create plan', 'error');
      }
    } catch (e) {
      showToast('Network error creating plan', 'error');
    }
  };

  const handleManualGrant = async () => {
    if (!grantData.user_id || !grantData.plan_id) {
      showToast('Select student and plan', 'error');
      return;
    }

    try {
      const res = await fetch('/api/subscriptions.php?action=manual_grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(grantData),
      });
      const data = await res.json();

      if (data.success) {
        showToast('Subscription granted to student', 'success');
        setIsGrantModalOpen(false);
        fetchSubscriptionsAndTx();
      } else {
        showToast(data.error || 'Failed to grant subscription', 'error');
      }
    } catch (e) {
      showToast('Network error granting subscription', 'error');
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#111111] tracking-tight">SaaS Subscription Architecture</h1>
          <p className="text-xs sm:text-sm text-[#5F6368] mt-0.5">
            Manage category-specific subscription plans, entitlements, student access ledgers, and transaction records.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setIsGrantModalOpen(true)}>
            Manual Grant
          </Button>
          <Button variant="primary" size="sm" onClick={() => setIsCreatePlanModalOpen(true)} leftIcon={<Plus className="w-4 h-4" />}>
            Create Plan
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'plans', label: 'Subscription Plans', badge: plans.length },
          { id: 'subscriptions', label: 'User Subscriptions', badge: subscriptions.length },
          { id: 'transactions', label: 'Transaction Ledger', badge: transactions.length },
        ]}
        activeTab={activeTab}
        onChange={(t) => setActiveTab(t as any)}
        variant="line"
      />

      {/* TAB 1: PLANS */}
      {activeTab === 'plans' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {plans.map((plan) => (
            <Card key={plan.id} variant="default" className="flex flex-col justify-between">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="primary">{plan.sector_name || `Sector #${plan.sector_id}`}</Badge>
                  <Badge variant={plan.status === 'active' ? 'success' : 'neutral'}>{plan.status}</Badge>
                </div>
                <CardTitle className="mt-2">{plan.name}</CardTitle>
                <CardDescription>Code: {plan.code} • {plan.duration_days} Days Access</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-2xl font-black text-[#111111]">
                  ₹{plan.price}{' '}
                  <span className="text-xs font-normal text-[#5F6368]">/ {plan.duration_days} days</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">Included Entitlements</span>
                  <div className="flex flex-wrap gap-1">
                    {(plan.entitlements || ['practice_access', 'revision_access']).map((ent) => (
                      <span key={ent} className="px-2 py-0.5 text-[10px] font-semibold bg-[#E8FAF9] text-[#1b8e88] rounded-md border border-[#56E8E1]/30">
                        {ent.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {plans.length === 0 && !isLoading && (
            <div className="col-span-full py-12 text-center text-xs text-[#5F6368]">
              No subscription plans configured yet. Click "Create Plan" to add category plans.
            </div>
          )}
        </div>
      )}

      {/* TAB 2: USER SUBSCRIPTIONS */}
      {activeTab === 'subscriptions' && (
        <Card variant="default">
          <CardHeader>
            <CardTitle>Active User Subscriptions</CardTitle>
            <CardDescription>Category-specific user subscriptions granted or purchased.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-[#E5E9E9] text-[#5F6368] uppercase font-bold text-[10px]">
                    <th className="py-2.5 px-3">Student</th>
                    <th className="py-2.5 px-3">Plan & Sector</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3">Valid Until</th>
                    <th className="py-2.5 px-3">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E9E9]">
                  {subscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-[#F7FAFA]">
                      <td className="py-3 px-3">
                        <div className="font-bold text-[#111111]">{sub.user_name || `User #${sub.user_id}`}</div>
                        <div className="text-[10px] text-[#5F6368]">{sub.user_mobile}</div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="font-bold text-[#1b8e88]">{sub.plan_name}</div>
                        <div className="text-[10px] text-[#5F6368]">{sub.sector_name}</div>
                      </td>
                      <td className="py-3 px-3">
                        <Badge variant="success" size="sm">{sub.status}</Badge>
                      </td>
                      <td className="py-3 px-3 font-medium text-[#111111]">
                        {new Date(sub.end_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-3 uppercase text-[10px] font-bold text-[#5F6368]">
                        {sub.provider}
                      </td>
                    </tr>
                  ))}
                  {subscriptions.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-[#5F6368]">No active student subscriptions found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TAB 3: TRANSACTIONS */}
      {activeTab === 'transactions' && (
        <Card variant="default">
          <CardHeader>
            <CardTitle>Subscription Transaction Ledger</CardTitle>
            <CardDescription>Records of subscription purchases and manual grants.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-[#E5E9E9] text-[#5F6368] uppercase font-bold text-[10px]">
                    <th className="py-2.5 px-3">Student</th>
                    <th className="py-2.5 px-3">Plan</th>
                    <th className="py-2.5 px-3">Amount</th>
                    <th className="py-2.5 px-3">Type</th>
                    <th className="py-2.5 px-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E9E9]">
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-[#F7FAFA]">
                      <td className="py-3 px-3 font-bold text-[#111111]">{tx.user_name || `User #${tx.user_id}`}</td>
                      <td className="py-3 px-3 text-[#1b8e88] font-bold">{tx.plan_name}</td>
                      <td className="py-3 px-3 font-black text-[#111111]">₹{tx.amount}</td>
                      <td className="py-3 px-3 uppercase text-[10px] font-bold text-[#5F6368]">{tx.transaction_type}</td>
                      <td className="py-3 px-3 text-[#5F6368]">{new Date(tx.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {transactions.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-[#5F6368]">No transaction records found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal: Create Plan */}
      <Modal
        isOpen={isCreatePlanModalOpen}
        onClose={() => setIsCreatePlanModalOpen(false)}
        title="Create Category Subscription Plan"
        description="Configure a new exam-specific subscription plan."
        footer={
          <>
            <Button variant="outline" onClick={() => setIsCreatePlanModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreatePlan}>Create Plan</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Target Exam Sector"
            value={newPlan.sector_id}
            onChange={(e) => setNewPlan({ ...newPlan, sector_id: Number(e.target.value) })}
            options={sectors.map((s) => ({ label: `${s.name} (${s.code})`, value: s.id }))}
          />
          <Input
            label="Plan Name"
            placeholder="e.g. JPSC Pro Annual"
            value={newPlan.name}
            onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
          />
          <Input
            label="Plan Code"
            placeholder="e.g. JPSC_PRO_ANNUAL"
            value={newPlan.code}
            onChange={(e) => setNewPlan({ ...newPlan, code: e.target.value.toUpperCase() })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Price (₹)"
              type="number"
              value={newPlan.price}
              onChange={(e) => setNewPlan({ ...newPlan, price: Number(e.target.value) })}
            />
            <Input
              label="Duration (Days)"
              type="number"
              value={newPlan.duration_days}
              onChange={(e) => setNewPlan({ ...newPlan, duration_days: Number(e.target.value) })}
            />
          </div>
        </div>
      </Modal>

      {/* Modal: Manual Grant */}
      <Modal
        isOpen={isGrantModalOpen}
        onClose={() => setIsGrantModalOpen(false)}
        title="Manual Subscription Grant"
        description="Grant a category subscription directly to a student."
        footer={
          <>
            <Button variant="outline" onClick={() => setIsGrantModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleManualGrant}>Grant Access</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Select Student"
            value={grantData.user_id}
            onChange={(e) => setGrantData({ ...grantData, user_id: Number(e.target.value) })}
            options={users.filter((u) => u.role === 'student').map((u) => ({ label: `${u.name} (${u.mobile})`, value: u.id }))}
          />
          <Select
            label="Select Plan"
            value={grantData.plan_id}
            onChange={(e) => setGrantData({ ...grantData, plan_id: Number(e.target.value) })}
            options={plans.map((p) => ({ label: `${p.name} — ${p.sector_name} (₹${p.price})`, value: p.id }))}
          />
          <Input
            label="Access Duration (Days)"
            type="number"
            value={grantData.duration_days}
            onChange={(e) => setGrantData({ ...grantData, duration_days: Number(e.target.value) })}
          />
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionManager;
